import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { Box, Typography, Alert, Snackbar, Paper, Button, Backdrop, CircularProgress, Fade, Slide } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { getTicketsByColumn, updateTicket, clearDateFilters, getTicketsByBoardBatchPaginated } from '../../../reducers/kanban/ticketActions';
import { UPDATE_COLUMN_PAGINATION } from '../../../reducers/kanban/kanbanTypes';
import { getColumnsPaginated, updateColumnPositionsBatch } from '../../../reducers/kanban/columnActions';
import KanbanColumn from './KanbanColumn';
import { useToken } from '../../../hooks/useToken';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Contenedores principales
const KanbanContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'auto', // Cambio de 100% a auto para eliminar espacio extra
  width: '100%',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
}));

const ScrollWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  height: 'auto', // Altura automática para ajustarse al contenido
  minHeight: 'auto', // Sin altura mínima fija
  overflow: 'hidden',
}));

const ColumnsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  overflowY: 'hidden',
  padding: theme.spacing(1), // Reducido de 2 a 1
  paddingTop: theme.spacing(0.5), // Muy poco espacio arriba
  paddingBottom: theme.spacing(0.5), // Muy poco espacio abajo
  height: 'auto', // Altura automática basada en el contenido
  gap: theme.spacing(2),
  width: '100%',
  // Scrollbar horizontal más visible y mejor posicionado
  '&::-webkit-scrollbar': {
    height: 14, // Más grueso y visible
    display: 'block',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `2px solid ${theme.palette.divider}`,
    margin: `0 ${theme.spacing(2)}`, // Margen horizontal para que no toque los bordes
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.primary.main, // Color más visible
    borderRadius: 8,
    border: `2px solid ${theme.palette.background.paper}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  scrollbarWidth: 'auto', // Más visible en Firefox
  scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.paper}`,
}));

// Contenedor del Droppable con estilos para drag & drop usando Material-UI
const DroppableColumnsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDraggingOver'
})(({ theme, isDraggingOver }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  width: '100%',
  height: '100%',
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.short,
  }),
  backgroundColor: isDraggingOver 
    ? theme.palette.action.hover 
    : 'transparent',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5),
}));

const EmptyColumnMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
}));

/**
 * Tablero Kanban con funcionalidad de arrastrar y soltar para columnas y tickets
 * Implementa optimización batch para reordenamiento de columnas (reduce peticiones HTTP del 80-90%)
 * @param {string} boardType - Tipo de tablero ('general' o 'support')
 * @param {Array} columns - Columnas a mostrar
 * @param {Function} onDragEnd - Callback para eventos de arrastre completados
 * @param {Function} onTicketCreated - Callback opcional para cuando se crea un ticket
 */
const KanbanBoard = ({ boardType, columns = [], onDragEnd, onTicketCreated }, ref) => {
  const dispatch = useDispatch();
  const { getTokenSilent } = useToken();
  const columnsContainerRef = useRef(null);
  const loggedInAgent = useSelector((state) => state.agent);

  // Adaptación a la nueva estructura del estado después de la refactorización
  const { tickets: { filters = {}, loading: ticketsLoading = false } = { filters: {}, loading: false } } = useSelector(state => state.kanban);
  const { loading: columnsLoading } = useSelector(state => state.kanban.columns);
  
  const loadedColumnsRef = useRef(new Set());

  // Referencias para el auto-scroll y arrastre
  const autoScrollIntervalRef = useRef(null);
  const isDraggingRef = useRef(false); 
  const draggingTypeRef = useRef(null);
  const columnsUpdateTimeoutRef = useRef(null);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [hasMatchingTickets, setHasMatchingTickets] = useState(true);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'warning'
  });

  // Estado para el loading overlay durante el movimiento de tickets
  const [isMovingTicket, setIsMovingTicket] = useState(false);

  // Estado para el movimiento de columnas (simple)
  const [isMovingColumn] = useState(false);

  // Estados para scroll infinito de columnas
  const [columnsPagination, setColumnsPagination] = useState({
    offset: 0,
    limit: 5,
    hasMore: true,
    loading: false,
    totalLoaded: 0
  });

  // Estado para mostrar el modal de carga con delay
  const [showLoadingModal, setShowLoadingModal] = useState(false);

  // Referencias para evitar bucles infinitos
  const initialLoadTriggeredRef = useRef(false);
  const columnsHashRef = useRef('');
  
  // Ref para detectar si el componente está montado (prevenir memory leaks)
  const isMountedRef = useRef(true);

  // Efecto para sincronizar el estado de loading con la carga inicial
  useEffect(() => {
    if (!ticketsLoading && initialLoadTriggeredRef.current && !initialLoadComplete) {
      // Si la carga de tickets terminó y ya se había iniciado la carga inicial
      setInitialLoadComplete(true);
    }
  }, [ticketsLoading, initialLoadComplete]);

  // Efecto para mostrar el modal de carga con delay (evitar flashes)
  useEffect(() => {
    let timeoutId;
    
    // Mostrar modal cuando hay carga activa (inicial, filtros, o limpieza)
    if (ticketsLoading) {
      // Mostrar el modal después de 300ms para evitar flashes en cargas rápidas
      timeoutId = setTimeout(() => {
        setShowLoadingModal(true);
      }, 300);
    } else {
      // Ocultar inmediatamente cuando termine la carga
      setShowLoadingModal(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [ticketsLoading]);

  // Cleanup para prevenir memory leaks
  useEffect(() => {
    return () => {
      // Marcar componente como desmontado
      isMountedRef.current = false;
      
      // Limpiar intervals y timeouts
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      
      if (columnsUpdateTimeoutRef.current) {
        clearTimeout(columnsUpdateTimeoutRef.current);
        columnsUpdateTimeoutRef.current = null;
      }
    };
  }, []);

  // Función para recargar una columna específica
  const reloadColumnTickets = useCallback(async (columnId) => {
    if (!columnId) return;

    try {
      const token = await getTokenSilent();

      // Eliminar la columna de las ya cargadas para forzar recarga
      loadedColumnsRef.current.delete(columnId);

      // Recargar los tickets de la columna
      await dispatch(getTicketsByColumn(columnId, token, filters));

      // Si tenemos un callback, informar que se recargó la columna
      if (onTicketCreated) {
        onTicketCreated(columnId);
      }
    } catch (error) {
      console.error(`Error al recargar tickets para la columna ${columnId}:`, error);
    }
  }, [dispatch, getTokenSilent, filters, onTicketCreated]);

  // Función para cargar más columnas (scroll infinito)
  const loadMoreColumns = useCallback(async (boardId) => {
    if (!boardId || columnsPagination.loading || !columnsPagination.hasMore) {
      return;
    }

    try {
      if (isMountedRef.current) {
        setColumnsPagination(prev => ({ ...prev, loading: true }));
      }
      const token = await getTokenSilent();
      
      const nextOffset = columnsPagination.totalLoaded;
      const result = await dispatch(getColumnsPaginated(
        boardId, 
        nextOffset, 
        columnsPagination.limit, 
        token
      ));

      if (result && !result.error) {
        if (isMountedRef.current) {
          setColumnsPagination(prev => ({
            ...prev,
            offset: nextOffset,
            totalLoaded: result.totalLoaded || prev.totalLoaded,
            hasMore: result.hasMore || false,
            loading: false
          }));
        }

        // Cargar tickets para las nuevas columnas usando board_id (más eficiente)
        if (result.newColumns && result.newColumns.length > 0) {
          // Usar board_id para cargar todos los tickets de una vez
          await dispatch(getTicketsByBoardBatchPaginated(
            boardId, 
            token, 
            filters,
            { limit: 5, offset: 0 }  // Opcional, el backend ya usa estos valores por defecto
          ));
        }
      } else {
        if (isMountedRef.current) {
          setColumnsPagination(prev => ({ ...prev, loading: false, hasMore: false }));
        }
      }
    } catch (error) {
      console.error('Error al cargar más columnas:', error);
      if (isMountedRef.current) {
        setColumnsPagination(prev => ({ ...prev, loading: false, hasMore: false }));
      }
    }
  }, [dispatch, getTokenSilent, filters, columnsPagination.loading, columnsPagination.hasMore, columnsPagination.totalLoaded, columnsPagination.limit]);

  // Función para detectar scroll al límite derecho
  const handleHorizontalScroll = useCallback((event) => {
    const container = event.target;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    // Detectar si estamos cerca del final (dentro de 100px del borde derecho)
    const isNearEnd = scrollLeft + clientWidth >= scrollWidth - 100;
    
    if (isNearEnd && columnsPagination.hasMore && !columnsPagination.loading && columns.length > 0) {
      const boardId = columns[0]?.board_id;
      if (boardId) {
        loadMoreColumns(boardId);
      }
    }
  }, [columns, columnsPagination.hasMore, columnsPagination.loading, loadMoreColumns]);

  // Exponer la función de recarga al componente padre
  useImperativeHandle(
    ref,
    () => ({
      reloadColumnTickets,
      loadMoreColumns
    }),
    [reloadColumnTickets, loadMoreColumns]
  );

  // Effect para resetear paginación cuando cambia el board
  const currentBoardId = columns.length > 0 ? columns[0]?.board_id : null;
  useEffect(() => {
    if (columns && columns.length > 0) {
      const boardId = columns[0]?.board_id;
      if (boardId) {
        if (isMountedRef.current) {
          setColumnsPagination(prev => ({
            ...prev,
            offset: 0,
            totalLoaded: columns.length,
            hasMore: columns.length >= prev.limit // Si cargamos exactamente el limit, podría haber más
          }));
          
          // Resetear el estado de carga inicial cuando cambia el board
          setInitialLoadComplete(false);
        }
        initialLoadTriggeredRef.current = false;
        columnsHashRef.current = '';
        loadedColumnsRef.current.clear();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBoardId]); // Solo cuando cambia el board

  // Ref para prevenir llamadas simultáneas
  const loadingTicketsRef = useRef(false);

  const loadTicketsForColumns = useCallback(async (columnsToProcess) => {
    if (!columnsToProcess || columnsToProcess.length === 0) {
      return;
    }

    // Prevenir llamadas simultáneas
    if (loadingTicketsRef.current) {
      return;
    }
    loadingTicketsRef.current = true;

    try {
      const token = await getTokenSilent();

      // Identificar columnas no cargadas (medida de seguridad adicional)
      const columnsToLoad = columnsToProcess.filter(column =>
        column &&
        column.id &&
        !loadedColumnsRef.current.has(column.id)
      );

      if (columnsToLoad.length === 0) {
        return;
      }

      // Usar petición batch optimizada con board_id
      const boardId = columnsToLoad[0]?.board_id || columns[0]?.board_id;
      
      if (!boardId) {
        console.error('No se pudo obtener el board_id para loadTicketsForColumns');
        // Fallback a cargar individualmente
        throw new Error('Board ID no disponible');
      }
      
      try {
        await dispatch(getTicketsByBoardBatchPaginated(
          boardId, 
          token, 
          filters,
          { limit: 5, offset: 0 }  // Opcional, el backend ya usa estos valores por defecto
        ));
        
        // Marcar todas las columnas como cargadas
        columnsToLoad.forEach(column => {
          loadedColumnsRef.current.add(column.id);
        });
        
      } catch (batchError) {
        console.warn(`[FALLBACK] Error en batch API, usando peticiones individuales:`, batchError);
        
        // FALLBACK: Si el batch falla, cargar individualmente (mantener compatibilidad)
        for (const column of columnsToLoad) {
          try {
            if (column.tickets?.length > 0 || loadedColumnsRef.current.has(column.id)) {
              loadedColumnsRef.current.add(column.id);
              continue;
            }

            await dispatch(getTicketsByColumn(column.id, token, filters));
            loadedColumnsRef.current.add(column.id);
          } catch (error) {
            console.error(`[FALLBACK] Error al cargar tickets para columna ${column.id}:`, error);
            loadedColumnsRef.current.add(column.id); // Evitar intentos repetidos
          }
        }
      }

      // Permitir que el estado se actualice completamente
      await new Promise(resolve => setTimeout(resolve, 200));

      // Verificar si hay tickets después de aplicar filtros - usar el estado actual de Redux
      // Nota: No podemos usar getState aquí directamente en el callback, usar columns como referencia
      if (isMountedRef.current) {
        setHasMatchingTickets(true); // Asumimos que hay tickets después de cargar
      }
      
    } catch (error) {
      console.error('Error al cargar tickets:', error);
    } finally {
      loadingTicketsRef.current = false;
    }
  }, [dispatch, getTokenSilent, filters, columns]);

  // Carga inicial de tickets
  useEffect(() => {
    // Si ya se completó la carga inicial, no hacer nada
    if (initialLoadComplete) {
      return;
    }

    // Si no hay columnas, esperar a que lleguen
    if (!columns || columns.length === 0) {
      return;
    }

    // Crear hash de columnas para evitar ejecuciones duplicadas
    const currentColumnsHash = JSON.stringify(columns.map(col => ({ id: col.id, position: col.position })));
    
    // Si ya se procesó este set de columnas, no volver a procesar
    if (initialLoadTriggeredRef.current && columnsHashRef.current === currentColumnsHash) {
      return;
    }

    // Marcar que ya se inició el proceso para estas columnas
    initialLoadTriggeredRef.current = true;
    columnsHashRef.current = currentColumnsHash;

    // Si todas las columnas ya tienen tickets, marcarlas como cargadas
    const columnsWithTickets = columns.filter(col => col.tickets && col.tickets.length > 0);

    if (columnsWithTickets.length === columns.length && columns.length > 0) {
      columns.forEach(col => {
        if (col && col.id) loadedColumnsRef.current.add(col.id);
      });
      setInitialLoadComplete(true);
      return;
    }

    // LÓGICA MOVIDA DIRECTAMENTE AQUÍ para evitar dependencia de función
    const loadTicketsDirectly = async () => {
      try {
        const token = await getTokenSilent();

        // Identificar columnas no cargadas
        const columnsToLoad = columns.filter(column =>
          column &&
          column.id &&
          !loadedColumnsRef.current.has(column.id)
        );

        if (columnsToLoad.length === 0) {
          setInitialLoadComplete(true);
          return;
        }

        // Obtener el board_id de las columnas (todas tienen el mismo board_id)
        const boardId = columnsToLoad[0]?.board_id || columns[0]?.board_id;
        
        if (!boardId) {
          console.error('No se pudo obtener el board_id para la carga batch');
          // Fallback a cargar individualmente
          throw new Error('Board ID no disponible');
        }
        
        try {
          await dispatch(getTicketsByBoardBatchPaginated(
            boardId, 
            token, 
            filters,
            { limit: 5, offset: 0 }  // Opcional, el backend ya usa estos valores por defecto
          ));
          
          // Inicializar paginación para cada columna
          columnsToLoad.forEach(column => {
            // Marcar como cargada
            loadedColumnsRef.current.add(column.id);
            
            // Inicializar información de paginación para cada columna
            dispatch({
              type: UPDATE_COLUMN_PAGINATION,
              payload: {
                columnId: column.id,
                pagination: {
                  currentPage: 1,
                  totalTickets: 5, // Estimación inicial
                  hasMore: true // Asumimos que hay más hasta probar lo contrario
                }
              }
            });
          });
          
        } catch (batchError) {
          console.warn(`[FALLBACK] Error en batch API, usando peticiones individuales:`, batchError);
          
          // FALLBACK: Si el batch falla, cargar individualmente
          for (const column of columnsToLoad) {
            try {
              if (column.tickets?.length > 0 || loadedColumnsRef.current.has(column.id)) {
                loadedColumnsRef.current.add(column.id);
                // Inicializar paginación también en este caso
                dispatch({
                  type: UPDATE_COLUMN_PAGINATION,
                  payload: {
                    columnId: column.id,
                    pagination: {
                      currentPage: 1,
                      totalTickets: column.tickets?.length || 5,
                      hasMore: true
                    }
                  }
                });
                continue;
              }

              await dispatch(getTicketsByColumn(column.id, token, filters));
              loadedColumnsRef.current.add(column.id);
              
              // Inicializar paginación
              dispatch({
                type: UPDATE_COLUMN_PAGINATION,
                payload: {
                  columnId: column.id,
                  pagination: {
                    currentPage: 1,
                    totalTickets: 5,
                    hasMore: true
                  }
                }
              });
            } catch (error) {
              console.error(`[FALLBACK] Error al cargar tickets para columna ${column.id}:`, error);
              loadedColumnsRef.current.add(column.id); // Evitar intentos repetidos
            }
          }
        }

        // Solo actualizar estado si el componente sigue montado
        if (isMountedRef.current) {
          setInitialLoadComplete(true);
        }
        
      } catch (error) {
        console.error('❌ Error al cargar tickets:', error);
        // Solo actualizar estado si el componente sigue montado
        if (isMountedRef.current) {
          setInitialLoadComplete(true); // Marcar como completado para evitar bucles infinitos
        }
      }
    };

    // Ejecutar la carga directamente
    loadTicketsDirectly();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.length, currentBoardId]); // Solo cuando cambia la cantidad o el board, no todo el array

  // Evitar bucles infinitos al cambiar filtros
  const prevFiltersRef = useRef(null);

  // Recargar tickets cuando cambian los filtros de fecha
  useEffect(() => {
    if (!initialLoadComplete) {
      return;
    }

    // Evitar recargas innecesarias si los filtros no cambiaron
    const currentFiltersStr = JSON.stringify(filters);
    const prevFiltersStr = prevFiltersRef.current ? JSON.stringify(prevFiltersRef.current) : null;

    if (prevFiltersStr === currentFiltersStr) {
      return;
    }

    prevFiltersRef.current = filters;

    // Control para evitar recargas múltiples
    let isReloading = false;

    const reloadTickets = async () => {
      if (isReloading) {
        return;
      }
      isReloading = true;

      try {
        const token = await getTokenSilent();

        loadedColumnsRef.current.clear();

        if (filters && (filters.startDate || filters.endDate || filters.assignedAgent)) {
          try {
            // Usar método batch optimizado con board_id
            const boardId = columns[0]?.board_id;
            if (boardId) {
              const batchResult = await dispatch(getTicketsByBoardBatchPaginated(
                boardId, 
                token, 
                filters,
                { limit: 5, offset: 0 }  // Opcional, el backend ya usa estos valores por defecto
              ));
              
              // Marcar todas las columnas como cargadas e inicializar paginación
              columns.forEach(column => {
                if (column && column.id) {
                  loadedColumnsRef.current.add(column.id);
                  
                  // Reinicializar paginación cuando se aplican filtros
                  dispatch({
                    type: UPDATE_COLUMN_PAGINATION,
                    payload: {
                      columnId: column.id,
                      pagination: {
                        currentPage: 1,
                        totalTickets: 5, // Estimación inicial
                        hasMore: true
                      }
                    }
                  });
                }
              });
              
              // Verificar si hay tickets directamente del resultado de la función batch
              const hasTickets = Object.values(batchResult || {}).some(tickets => tickets && tickets.length > 0);
              if (isMountedRef.current) {
                setHasMatchingTickets(hasTickets);
              }
            }
          } catch (filterError) {
            console.error('Error al filtrar tickets con batch:', filterError);
            if (isMountedRef.current) {
              setHasMatchingTickets(true);
            }
            // Evitar llamada inmediata que pueda causar bucle
            if (!loadingTicketsRef.current) {
              setTimeout(() => {
                if (!loadingTicketsRef.current) {
                  loadTicketsForColumns(columns);
                }
              }, 500);
            }
          }
        } else {
          // Filtros limpiados: clearDateFilters ya se encargó de la recarga
          if (isMountedRef.current) {
            setHasMatchingTickets(true);
          }
          
          // clearDateFilters() ya actualizó Redux directamente, no necesitamos recargar aquí
          // Solo limpiar las referencias para que las columnas reconozcan los nuevos datos
          loadedColumnsRef.current.clear();
        }
      } catch (error) {
        console.error('Error al recargar tickets:', error);
      } finally {
        isReloading = false;
      }
    };

    // Debounce para evitar múltiples llamadas
    const timeoutId = setTimeout(reloadTickets, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, initialLoadComplete]); // Removida loadTicketsForColumns para evitar bucles

  const normalizedBoardType = boardType === 'support' ? 'soporte' : boardType;
  const sortedColumns = [...columns].sort((a, b) => (a.position || 0) - (b.position || 0));

  // Control para detectar nuevas columnas sin crear bucles infinitos
  const detectNewColumnsRef = useRef(false);
  const lastColumnsCountRef = useRef(columns.length);

  // Control para evitar múltiples llamadas batch durante cambios de columnas
  const lastColumnsHashRef = useRef('');

  // Actualizar estado de tickets cuando cambian las columnas
  useEffect(() => {
    if (columns.length > 0 && initialLoadComplete) {
      // Crear hash de las columnas para evitar ejecuciones innecesarias
      const currentColumnsHash = JSON.stringify(columns.map(col => ({ id: col.id, position: col.position })));
      
      if (currentColumnsHash === lastColumnsHashRef.current) {
        return;
      }
      
      lastColumnsHashRef.current = currentColumnsHash;

      // Limpiar timeout anterior si existe
      if (columnsUpdateTimeoutRef.current) {
        clearTimeout(columnsUpdateTimeoutRef.current);
      }

      // Debounce para evitar múltiples ejecuciones rápidas
      columnsUpdateTimeoutRef.current = setTimeout(() => {
        const hasTickets = columns.some(col => col.tickets && col.tickets.length > 0);
        const hasActiveFilters = filters && (filters.startDate || filters.endDate);

        if (hasActiveFilters || hasTickets) {
          setHasMatchingTickets(hasTickets);
        } else if (!hasTickets) {
          // Detectar columnas recién creadas manualmente vs cargadas desde servidor
          const columnsNeedingLoad = columns.filter(col => 
            col.id && 
            !loadedColumnsRef.current.has(col.id) && 
            (!col.tickets || col.tickets.length === 0)
          );

          if (columnsNeedingLoad.length > 0) {
            // Si solo hay UNA columna nueva sin tickets, probablemente se creó manualmente
            if (columnsNeedingLoad.length === 1) {
              // Marcar como cargada sin hacer petición
              columnsNeedingLoad.forEach(col => {
                if (col.id) loadedColumnsRef.current.add(col.id);
              });
              setHasMatchingTickets(true);
            } else {
              // Si todas las columnas están vacías (carga inicial) o hay múltiples sin cargar
              const allColumnsEmpty = columnsNeedingLoad.length === columns.length;
              
              if (allColumnsEmpty) {
                // Carga inicial: todas están vacías
                columnsNeedingLoad.forEach(col => {
                  if (col.id) loadedColumnsRef.current.add(col.id);
                });
                setHasMatchingTickets(true);
              } else {
                // Solo cargar si hay columnas mixtas (algunas con tickets, otras sin)
                if (!loadingTicketsRef.current) {
                  loadTicketsForColumns(columnsNeedingLoad);
                }
                setHasMatchingTickets(true);
              }
            }
          } else {
            setHasMatchingTickets(false);
          }
        }

        // Detectar si se ha eliminado una columna
        if (lastColumnsCountRef.current > columns.length) {
          lastColumnsCountRef.current = columns.length;

          // Limpiar las referencias de columnas cargadas
          loadedColumnsRef.current.clear();

          // Marcar las columnas actuales como cargadas
          columns.forEach(column => {
            if (column && column.id) {
              loadedColumnsRef.current.add(column.id);
            }
          });
        }
      }, 300); // Debounce de 300ms
    }

    // Cleanup al desmontar
    return () => {
      if (columnsUpdateTimeoutRef.current) {
        clearTimeout(columnsUpdateTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.length, initialLoadComplete]); // Simplificadas las dependencias para evitar bucles

  useEffect(() => {
    if (!initialLoadComplete) {
      return;
    }
    if (detectNewColumnsRef.current) {
      return;
    }

    detectNewColumnsRef.current = true;

    const currentColumnsCount = columns.length;
    const columnsAdded = currentColumnsCount > lastColumnsCountRef.current;
    lastColumnsCountRef.current = currentColumnsCount;

    // Ejecutar después del renderizado completo
    const timer = setTimeout(() => {
      const newColumnsToLoad = columns.filter(
        column => column && column.id && !loadedColumnsRef.current.has(column.id)
      );

      if (columnsAdded || newColumnsToLoad.length > 0) {
        if (columnsAdded) {
          loadedColumnsRef.current.clear();

          columns.forEach(column => {
            if (column && column.id) loadedColumnsRef.current.add(column.id);
          });

          if (!loadingTicketsRef.current) {
            loadTicketsForColumns(columns);
          }
          setHasMatchingTickets(true);
        } else {
          newColumnsToLoad.forEach(column => {
            if (column && column.id) loadedColumnsRef.current.add(column.id);
          });

          if (!loadingTicketsRef.current) {
            loadTicketsForColumns(newColumnsToLoad);
          }
        }
      }

      detectNewColumnsRef.current = false;
    }, 500);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.length, initialLoadComplete]); // Simplificadas las dependencias

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };
  }, []);

  // Auto-scroll durante arrastre
  const startAutoScroll = useCallback((clientX, isDraggingTicket = false) => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    if (!columnsContainerRef.current) return;

    const container = columnsContainerRef.current;
    const containerRect = container.getBoundingClientRect();

    const distanceFromLeft = clientX - containerRect.left;
    const distanceFromRight = containerRect.right - clientX;

    // Zona más amplia para tickets para facilitar arrastre a columnas lejanas
    const scrollTriggerZone = isDraggingTicket ? 250 : 120;

    let scrollDirection = 0;
    let scrollSpeed = 0;

    // Mayor velocidad para tickets para facilitar movimiento entre columnas
    const baseSpeed = isDraggingTicket ? 15 : 8;

    if (distanceFromLeft < scrollTriggerZone) {
      // Scroll izquierda (velocidad proporcional a cercanía al borde)
      scrollDirection = -1;
      scrollSpeed = Math.max(3, baseSpeed * (1 - distanceFromLeft / scrollTriggerZone));

      if (isDraggingTicket) {
        // Aumentos progresivos de velocidad para tickets
        if (distanceFromLeft < scrollTriggerZone / 2) scrollSpeed *= 1.5;
        if (distanceFromLeft < scrollTriggerZone / 4) scrollSpeed *= 1.5;
      }
    } else if (distanceFromRight < scrollTriggerZone) {
      // Scroll derecha (velocidad proporcional a cercanía al borde)
      scrollDirection = 1;
      scrollSpeed = Math.max(3, baseSpeed * (1 - distanceFromRight / scrollTriggerZone));

      if (isDraggingTicket) {
        // Aumentos progresivos de velocidad para tickets
        if (distanceFromRight < scrollTriggerZone / 2) scrollSpeed *= 1.5;
        if (distanceFromRight < scrollTriggerZone / 4) scrollSpeed *= 1.5;
      }
    }

    // Iniciar auto-scroll si estamos en una zona sensible
    if (scrollDirection !== 0) {
      // Intervalo más corto para tickets = movimiento más suave
      const interval = isDraggingTicket ? 8 : 16;

      autoScrollIntervalRef.current = setInterval(() => {
        container.scrollLeft += scrollDirection * scrollSpeed;
      }, interval);
    }
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);

  const handleDragStart = useCallback((start) => {
    stopAutoScroll();
    isDraggingRef.current = true;
    draggingTypeRef.current = start.type === 'ticket' ? 'ticket' : 'column';
  }, [stopAutoScroll]);

  const handleDragUpdate = useCallback((update) => {
    if (!update.clientX) return;

    // Determinar tipo de elemento arrastrado
    let isDraggingTicket = draggingTypeRef.current === 'ticket';

    // Respaldo si no tenemos el tipo en la referencia
    if (draggingTypeRef.current === null) {
      if (update.type === 'ticket' ||
          (update.source && update.source.droppableId !== 'all-columns')) {
        draggingTypeRef.current = 'ticket';
        isDraggingTicket = true;
      } else {
        draggingTypeRef.current = 'column';
      }
    }

    startAutoScroll(update.clientX, isDraggingTicket);
  }, [startAutoScroll]);

  // Verificar compatibilidad del tipo de tablero
  const isIncompatibleBoard = () => {
    return normalizedBoardType === 'bot'; 
  };

  // Verificar si el tipo de tablero permite mover columnas
  const isValidBoardType = () => {
    return ['general', 'soporte', 'flujo_bot'].includes(normalizedBoardType);
  };

  // Manejar fin del arrastre
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;

    // Si no hay destino, no hacer nada
    if (!destination) {
      return;
    }

    // Si se soltó en la misma posición, no hacer nada
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const token = await getTokenSilent();

    // Reordenar COLUMNAS
    if (type === 'column') {
      const newOrderedColumns = Array.from(columns);
      const [reorderedColumn] = newOrderedColumns.splice(source.index, 1);
      newOrderedColumns.splice(destination.index, 0, reorderedColumn);

      // Optimización: Actualizar estado local inmediatamente para UI fluida
      dispatch({
        type: 'REORDER_COLUMNS_OPTIMISTIC',
        payload: newOrderedColumns
      });

      // Preparar datos para la actualización batch en el backend
      const updates = newOrderedColumns.map((col, index) => ({
        id: col.id,
        position: index + 1, // Ajuste: la posición en el backend empieza en 1
      }));

      // Actualizar en el backend después de un breve delay
      try {
        await dispatch(updateColumnPositionsBatch(updates, token));
      } catch (error) {
        console.error('Error al actualizar posiciones de columnas:', error);
        // Podríamos revertir el estado si falla la API
      }
      return;
    }

    // Reordenar TICKETS
    if (type === 'ticket') {
      const sourceColumn = columns.find((c) => c.id === source.droppableId);
      const destColumn = columns.find((c) => c.id === destination.droppableId);

      if (!sourceColumn || !destColumn) {
        return;
      }

      const sourceTickets = Array.from(sourceColumn.tickets);
      const [movedTicket] = sourceTickets.splice(source.index, 1);

      // Si se mueve dentro de la misma columna
      if (source.droppableId === destination.droppableId) {
        sourceTickets.splice(destination.index, 0, movedTicket);
        // Lógica de reordenamiento de tickets dentro de una columna (si es necesario)
      } else {
        // Si se mueve a una columna diferente
        const destTickets = Array.from(destColumn.tickets);
        destTickets.splice(destination.index, 0, movedTicket);

        try {
          // Actualización optimista de la UI
          // No se necesita dispatch aquí, el estado local se maneja con la props 'columns'

          // Mostrar el spinner de carga
          if (isMountedRef.current) {
        setIsMovingTicket(true);
          }

          // Obtener el ID y nombre del agente que realiza el cambio
          const agentId = loggedInAgent?._id || loggedInAgent?.id;
          const agentName = loggedInAgent?.email
            ? loggedInAgent.email.split('@')[0]
            : loggedInAgent?.display_name || 'Agente';
        
          // Llamada a la API para actualizar el ticket
          await dispatch(
            updateTicket(
              draggableId,
              {
                column_id: destination.droppableId,
                changed_by_agent_id: agentId, // ID del agente que mueve
                changed_by_agent_name: agentName, // Nombre del agente que mueve
              },
              token,
            ),
          );
        
          // Opcional: Recargar los datos de las columnas afectadas si es necesario
          // await reloadColumnTickets(source.droppableId);
          // await reloadColumnTickets(destination.droppableId);
    } catch (error) {
          console.error('Error al mover el ticket:', error);
          setAlert({ open: true, message: 'Error al mover el ticket', severity: 'error' });
          // Revertir el estado si la API falla (lógica de rollback)
    } finally {
          // Ocultar el spinner
          if (isMountedRef.current) {
      setIsMovingTicket(false);
          }
    }
      }
    }
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({
      ...prev,
      open: false
    }));
  };

  if (isIncompatibleBoard()) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Este tipo de tablero no soporta vista Kanban
        </Typography>
      </Box>
    );
  }

  if (!isValidBoardType()) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Tipo de tablero no válido: {normalizedBoardType}
        </Typography>
      </Box>
    );
  }

  if (!columns || columns.length === 0) {
    return (
      <EmptyColumnMessage>
        <Typography variant="h6">
          No hay columnas disponibles. Use el botón "Añadir Columna" para crear una.
        </Typography>
      </EmptyColumnMessage>
    );
  }

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      // Parsear la fecha como local evitando problemas de zona horaria
      const [year, month, day] = dateString.split('-');
      const localDate = new Date(year, month - 1, day);
      return format(localDate, "d 'de' MMMM yyyy", { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  // Mensaje para filtros sin resultados
  const hasActiveFilters = filters && (filters.startDate || filters.endDate);
  if (hasActiveFilters && !hasMatchingTickets && initialLoadComplete) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            maxWidth: 600,
            mx: 'auto',
            backgroundColor: theme => theme.palette.background.default,
            border: theme => `1px solid ${theme.palette.primary.main}`
          }}
        >
          <FilterAltIcon color="primary" sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
          <Typography variant="h6" color="primary.main" gutterBottom>
            Sin resultados
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {filters.startDate && filters.endDate
              ? (filters.startDate === filters.endDate
                ? `No se encontraron tickets para ${formatDateForDisplay(filters.startDate)}`
                : `No se encontraron tickets entre ${formatDateForDisplay(filters.startDate)} y ${formatDateForDisplay(filters.endDate)}`)
              : filters.startDate
                ? `No se encontraron tickets a partir de ${formatDateForDisplay(filters.startDate)}`
                : `No se encontraron tickets anteriores a ${formatDateForDisplay(filters.endDate)}`
            }
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterAltIcon />}
            onClick={() => dispatch(clearDateFilters(getTokenSilent))}
            sx={{ mt: 2 }}
          >
            Limpiar filtros
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <KanbanContainer>
      {/* Indicador de scroll horizontal */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 0.5,
        color: 'text.secondary',
        fontSize: '0.75rem',
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '4px' }}>&#8678;</span>
          Desliza horizontalmente para ver todas las columnas
          {columnsPagination.hasMore && ` (cargando ${columns.length} de muchas más)`}
          <span style={{ marginLeft: '4px' }}>&#8680;</span>
        </Typography>
      </Box>
      <ScrollWrapper>
        <DragDropContext
          onDragStart={handleDragStart}
          onDragUpdate={handleDragUpdate}
          onDragEnd={handleDragEnd}
        >
          <Droppable
            droppableId="all-columns"
            direction="horizontal"
            type="column"
          >
            {(provided, snapshot) => (
              <ColumnsContainer
                ref={(el) => {
                  provided.innerRef(el);
                  columnsContainerRef.current = el;
                }}
                {...provided.droppableProps}
                onScroll={handleHorizontalScroll}
              >
                {/* Contenedor con estilos Material-UI para drag & drop */}
                <DroppableColumnsContainer
                  isDraggingOver={snapshot.isDraggingOver}
                  sx={{
                    // Ancho mínimo calculado para forzar scroll horizontal optimizado
                    width: `${Math.max(sortedColumns.length + (columnsPagination.hasMore ? 1 : 0), 6) * 290}px`, // Reducido de 350px a 290px
                  }}
                >
                  {sortedColumns.map((column, index) => (
                    <KanbanColumn
                      key={column.id}
                      column={column}
                      index={index}
                      boardType={normalizedBoardType}
                    />
                  ))}
                  {provided.placeholder}
                  
                  {/* Indicador de carga */}
                  {columnsPagination.loading && (
                    <Box
                      sx={{
                        minWidth: '280px', // Ajustado para coincidir con las columnas
                        maxWidth: '280px',
                        flex: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: 'calc(100vh - 500px)',
                          minHeight: 'calc(100vh - 500px)',
                          maxHeight: 'calc(100vh - 500px)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'background.paper',
                          borderRadius: 1,
                          border: theme => `1px solid ${theme.palette.divider}`,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          marginBottom: 0.5,
                          p: 2,
                        }}
                      >
                        <CircularProgress size={40} color="primary" />
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
                          Cargando columnas...
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {/* Indicador para cargar más */}
                  {!columnsPagination.loading && columnsPagination.hasMore && (
                    <Box
                      sx={{
                        minWidth: '280px', // Ajustado para coincidir con las columnas
                        maxWidth: '280px',
                        flex: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: 'calc(100vh - 500px)',
                          minHeight: 'calc(100vh - 500px)',
                          maxHeight: 'calc(100vh - 500px)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'background.paper',
                          borderRadius: 1,
                          border: theme => `1px solid ${theme.palette.divider}`,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          cursor: 'pointer',
                          p: 2,
                          marginBottom: 1,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            transform: 'translateY(-2px)',
                            borderColor: 'primary.main',
                          },
                        }}
                        onClick={() => {
                          if (sortedColumns.length > 0) {
                            const boardId = sortedColumns[0]?.board_id;
                            if (boardId) {
                              loadMoreColumns(boardId);
                            }
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <Box 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              backgroundColor: 'primary.light',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1
                            }}
                          >
                            <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold' }}>
                              +
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 500 }}>
                            Cargar más columnas
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center' }}>
                            Desliza o haz clic aquí
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </DroppableColumnsContainer>
              </ColumnsContainer>
            )}
          </Droppable>
        </DragDropContext>
      </ScrollWrapper>

      {/* Modal de carga inicial de tickets */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={showLoadingModal}
      >
        <Fade in={showLoadingModal}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            p: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            minWidth: 320,
            textAlign: 'center'
          }}>
            <Box sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CircularProgress 
                color="primary" 
                size={64} 
                thickness={4}
                sx={{
                  animationDuration: '1.2s'
                }}
              />
            </Box>
            
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 1,
                color: 'primary.main',
                fontWeight: 600
              }}
            >
              {filters && (filters.startDate || filters.endDate || filters.assignedAgent) 
                ? 'Aplicando Filtros'
                : 'Cargando Tickets'
              }
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                lineHeight: 1.5
              }}
            >
              {filters && (filters.startDate || filters.endDate || filters.assignedAgent)
                ? 'Estamos obteniendo los tickets filtrados...'
                : 'Estamos obteniendo los tickets del tablero...'
              }
            </Typography>
          </Box>
        </Fade>
      </Backdrop>

      {/* Loading overlay durante el movimiento de tickets o columnas */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.3)'
        }}
        open={isMovingTicket || isMovingColumn || columnsLoading}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          p: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: 2,
          boxShadow: 3
        }}>
          <CircularProgress color="primary" size={48} />
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 2, 
              color: 'primary.main',
              fontWeight: 500
            }}
          >
            {isMovingTicket ? 'Moviendo ticket...' : 
             isMovingColumn ? 'Reordenando columnas...' : 
             columnsLoading ? 'Reordenando columnas...' :
             'Procesando cambio...'}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1, 
              color: 'text.secondary',
              textAlign: 'center'
            }}
          >
            Por favor espera mientras procesamos el cambio
          </Typography>
        </Box>
      </Backdrop>

      {/* Notificaciones */}
      <Snackbar
        open={alert.open}
        autoHideDuration={alert.severity === 'success' ? 3000 : 6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          mt: 8,
          '& .MuiSnackbar-root': {
            top: '120px !important'
          }
        }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'down' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ 
            width: '100%',
            minWidth: '400px',
            fontSize: '1rem',
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 500
            },
            boxShadow: 3,
            ...(alert.severity === 'success' && {
              backgroundColor: 'success.light',
              color: 'success.contrastText',
              '& .MuiAlert-icon': {
                color: 'success.contrastText'
              }
            }),
            ...(alert.severity === 'info' && {
              backgroundColor: 'info.light',
              color: 'info.contrastText',
              '& .MuiAlert-icon': {
                color: 'info.contrastText'
              }
            })
          }}
          variant="filled"
        >
          {alert.message}
        </Alert>
      </Snackbar>

    </KanbanContainer>
  );
};

export default forwardRef(KanbanBoard);